class ComponentScroll extends Theme.Component {
  #state = "idle";
  #drag_start = 0;
  #drag_threshold = 10;
  #drag_delta = 0;
  #scroll_start = 0;
  #animation_frame_id = null;
  #snap_points = [];

  log(...args) {
    if (!window.__DEBUG_COMPONENT_SCROLL) {
      return;
    }

    console.log("[component-scroll]", ...args);
  }

  connectedCallback() {
    this.log("connected", { el: this });

    this.defineElements({
      track: ":scope > .scroll-track",
      dots: ":scope > .scroll-dots",
      button_prev: ":scope > .scroll-button-left",
      button_next: ":scope > .scroll-button-right"
    });

    this.defineElementCollections({
      items: ":scope > .scroll-track > *"
    });

    this.log("items", { count: this.elements.items?.length ?? 0 });

    this.addActionEvents();

    this.setStyles();
    this.setSnapPoints();

    this.log("initial-measure", {
      scrollWidth: this.elements.track?.scrollWidth,
      clientWidth: this.elements.track?.clientWidth,
      scrollLeft: this.elements.track?.scrollLeft
    });

    let scroll_width_after_raf = 0;

    requestAnimationFrame(() => {
      this.log("raf-measure", {
        scrollWidth: this.elements.track?.scrollWidth,
        clientWidth: this.elements.track?.clientWidth,
        scrollLeft: this.elements.track?.scrollLeft
      });

      this.setStyles();
      this.setSnapPoints();
      this.classList.add("state--ready");

      scroll_width_after_raf = this.elements.track?.scrollWidth ?? 0;

      this.log("raf-after-setStyles", {
        isScrollable: this.isScrollable(),
        hasScrollableClass: this.classList.contains("state--scrollable")
      });
    });

    setTimeout(() => {
      const current_scroll_width = this.elements.track?.scrollWidth ?? 0;

      if (current_scroll_width === scroll_width_after_raf) return;

      this.log("timeout-250ms-measure", {
        scrollWidth: this.elements.track?.scrollWidth,
        clientWidth: this.elements.track?.clientWidth,
        scrollLeft: this.elements.track?.scrollLeft
      });

      this.setStyles();
      this.setSnapPoints();

      this.log("timeout-250ms-after-setStyles", {
        isScrollable: this.isScrollable(),
        hasScrollableClass: this.classList.contains("state--scrollable")
      });
    }, 250);

    this.addDirectEvent("resize", window, Theme.Helpers.debounce(this.onResize.bind(this), 150));
    this.addDirectEvent("scroll", this.elements.track, Theme.Helpers.throttle(this.updateActiveDot.bind(this), 50));
    this.addDirectEvent("scroll", this.elements.track, Theme.Helpers.debounce(this.updateActiveDot.bind(this), 100));
    this.addDirectEvent("pointerdown", this.elements.track, this.onPointerDown.bind(this));
    this.addDirectEvent("pointermove", this.elements.track, this.onPointerMove.bind(this));
    this.addDirectEvent("pointerup", this.elements.track, this.onPointerUp.bind(this));
    this.addDirectEvent("pointercancel", this.elements.track, this.onPointerUp.bind(this));

    this.addDirectEvent("dragstart", this.elements.track, (event) => {
      event.preventDefault();
    });
  }

  setStyles() {
    const track = this.elements.track;
    const scrollWidth = track?.scrollWidth ?? 0;
    const clientWidth = track?.clientWidth ?? 0;
    const isScrollable = this.isScrollable();

    this.log("setStyles", {
      scrollWidth,
      clientWidth,
      delta: scrollWidth - clientWidth,
      isScrollable
    });

    if (isScrollable) {
      this.classList.add("state--scrollable");
      this.updateScrollbarCompensation();
      this.log("setStyles:add state--scrollable");
    } else {
      this.classList.remove("state--scrollable");
      this.log("setStyles:remove state--scrollable");
    }
  }

  updateScrollbarCompensation() {
    const scrollbar_width = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbar_width > 0) {
      this.style.setProperty("--scrollbar-compensation", `${scrollbar_width / 2}px`);
    } else {
      this.style.removeProperty("--scrollbar-compensation");
    }
  }

  setSnapPoints() {
    const track = this.elements.track;

    if (!track) {
      this.#snap_points = [];
      return;
    }

    const container_box = track.getBoundingClientRect();
    const container_style = getComputedStyle(track);
    const container_padding_left = parseFloat(container_style.paddingLeft) || 0;
    const container_edge = container_box.left + container_padding_left;
    const container_scroll = track.scrollLeft;
    const max = track.scrollWidth - track.clientWidth;

    const points = [];

    this.elements.items?.forEach((element) => {
      const element_box = element.getBoundingClientRect();
      const item_scroll = element_box.left - container_edge;
      const distance = container_scroll + item_scroll;
      const clamped = Math.max(0, Math.min(distance, max));
      const rounded = Math.round(clamped);

      if (!points.includes(rounded)) {
        points.push(rounded);
      }
    });

    this.#snap_points = points;
    this.buildDots();
  }

  buildDots() {
    const dots_el = this.elements.dots;
    if (!dots_el) return;

    dots_el.innerHTML = "";

    const snap_count = this.#snap_points.length;
    if (snap_count <= 1) return;

    const group_size = this.getGroupSize();
    const num_dots = Math.max(2, Math.ceil(snap_count / group_size));
    const snaps_per_dot = Math.ceil(snap_count / num_dots);

    for (let i = 0; i < num_dots; i++) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.classList.add("scroll-dot");
      const snap_index = Math.min(i * snaps_per_dot, snap_count - 1);
      dot.addEventListener("click", () => {
        this.animateScrollTo(this.#snap_points[snap_index]);
      });
      dots_el.appendChild(dot);
    }

    this.updateActiveDot();
    this.updateButtonVisibility();
  }

  updateActiveDot() {
    const dots_el = this.elements.dots;
    if (!dots_el) return;

    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const num_dots = dots_el.querySelectorAll(".scroll-dot").length;
    const snap_count = this.#snap_points.length;
    const snaps_per_dot = num_dots ? Math.ceil(snap_count / num_dots) : 1;

    let closest_snap = 0;
    let min_dist = Infinity;
    this.#snap_points.forEach((point, i) => {
      const dist = Math.abs(point - scroll_left);
      if (dist < min_dist) {
        min_dist = dist;
        closest_snap = i;
      }
    });

    const active_dot = Math.min(Math.floor(closest_snap / snaps_per_dot), num_dots - 1);

    dots_el.querySelectorAll(".scroll-dot").forEach((dot, i) => {
      dot.classList.toggle("state--active", i === active_dot);
    });

    this.updateButtonVisibility();
  }

  updateButtonVisibility() {
    if (!this.#snap_points.length) return;

    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const tolerance = 2;
    const first = this.#snap_points[0];
    const last = this.#snap_points[this.#snap_points.length - 1];

    this.elements.button_prev?.classList.toggle("state--hidden", scroll_left <= first + tolerance);
    this.elements.button_next?.classList.toggle("state--hidden", scroll_left >= last - tolerance);
  }

  isScrollable() {
    const track = this.elements.track;
    return track ? (track.scrollWidth - track.clientWidth) > 1 : false;
  }

  onResize() {
    this.setStyles();
    this.setSnapPoints();
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.#state = "tracking";
    this.#scroll_start = this.elements.track?.scrollLeft ?? 0;
    this.#drag_start = event.pageX;
  }

  onPointerMove(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    this.#drag_delta = event.pageX - this.#drag_start;

    if (this.isDrag() && this.#state === "tracking") {
      this.elements.track?.setPointerCapture(event.pointerId);
      this.#state = "dragging";
      this.classList.add("state--dragging");
      this.startAnimationLoop();
    }
  }

  onPointerUp(event) {
    if (event.pointerType === "touch") {
      this.reset();
      return;
    }

    const distance = Math.abs((this.elements.track?.scrollLeft ?? 0) - this.#scroll_start);

    if (this.#state === "dragging" && event.pointerType === "mouse") {
      this.preventDefaultClick();
    }

    if (this.#state === "dragging" || distance >= this.#drag_threshold) {
      this.animateScrollTo(this.getDirectionalSnapPoint());
    }

    this.reset();
  }

  isDrag() {
    return Math.abs(this.#drag_delta) >= this.#drag_threshold;
  }

  startAnimationLoop() {
    if (this.#animation_frame_id) {
      return;
    }

    const loop = () => {
      if (this.elements.track) {
        this.elements.track.scrollLeft = this.#scroll_start - this.#drag_delta;
      }
      this.#animation_frame_id = requestAnimationFrame(loop);
    };

    this.#animation_frame_id = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    cancelAnimationFrame(this.#animation_frame_id);
    this.#animation_frame_id = null;
  }

  preventDefaultClick() {
    this.elements.track?.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    }, {
      capture: true,
      once: true
    });
  }

  animateScrollTo(value) {
    this.elements.track?.scrollTo({
      left: value,
      behavior: "smooth"
    });
  }

  getDirectionalSnapPoint() {
    const container_scroll = this.elements.track?.scrollLeft ?? 0;

    if (!this.#snap_points.length) {
      return container_scroll;
    }

    const direction = Math.sign(container_scroll - this.#scroll_start);

    if (direction > 0) {
      return this.getNextSnapPoint();
    }

    if (direction < 0) {
      return this.getPreviousSnapPoint();
    }

    return container_scroll;
  }

  getNextSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const next = points.find((p) => p > scroll_left);

    return next ?? points.at(-1);
  }

  getPreviousSnapPoint() {
    const points = this.#snap_points;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;
    const previous = points.toReversed().find((p) => p < scroll_left);

    return previous ?? points[0];
  }

  nextItem() {
    this.animateScrollTo(this.getNextSnapPoint());
  }

  previousItem() {
    this.animateScrollTo(this.getPreviousSnapPoint());
  }

  nextGroup() {
    if (!this.#snap_points.length) return;

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    const index = Math.min(current_index + group_size, this.#snap_points.length - 1);

    this.animateScrollTo(this.#snap_points[index]);
  }

  previousGroup() {
    if (!this.#snap_points.length) return;

    const group_size = this.getGroupSize();
    const tolerance = 0.5;
    const scroll_left = this.elements.track?.scrollLeft ?? 0;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (scroll_left - tolerance);
    });

    const index = Math.max(current_index - group_size, 0);

    this.animateScrollTo(this.#snap_points[index]);
  }

  getGroupSize() {
    const track = this.elements.track;
    const items = this.elements.items;

    if (!track || !items?.length) return 1;

    const container_width = track.clientWidth;
    const item_width = items[0].clientWidth;

    return item_width ? Math.max(1, Math.floor(container_width / item_width)) : 1;
  }

  reset() {
    this.stopAnimationLoop();
    this.#state = "idle";
    this.#drag_start = 0;
    this.#scroll_start = 0;
    this.classList.remove("state--dragging");
  }
}

customElements.define("component-scroll", ComponentScroll);
